/**
 * Sales Performance round 2, stage 2: customer size bands, the mix of bands behind each salesperson, and the
 * expected reach that replaces a flat category target once band targets exist.
 *
 * Pure and side-effect free: the browser, the derived-data script and (stage 5) the backend share it.
 *
 * The bands are SIMULATED from spend until Flash chooses real customer types. Real types replace them later by
 * writing the same documents with different keys, so nothing here knows the words large / medium / small
 * beyond DEFAULT_BANDS: every function works on whatever band keys it is handed.
 *
 * Only counts leave this file. A customer's account code is a map key while a calculation runs and is never
 * in a result.
 */

export interface BandSpec {
    key: string;
    /** share of ranked customers, biggest spenders first; the shares of a spec add up to 1 */
    share: number;
}

/** Large = top 20% of customers by net sales, Medium = the next 30%, Small = the rest. */
export const DEFAULT_BANDS: BandSpec[] = [
    { key: 'large', share: 0.2 },
    { key: 'medium', share: 0.3 },
    { key: 'small', share: 0.5 },
];

/** band key -> number of customers (or active customers) in it */
export type BandCounts = Record<string, number>;

/** A customer's month with one salesperson, as far as this file reads it. */
export interface SegCustomer {
    sales: number;
    cats?: Record<string, { sales: number }>;
}

/** salesperson -> account -> that customer's month */
export type SegLists = Record<string, Record<string, SegCustomer>>;

export interface BandReachCell {
    buyers: number;
    active: number;
}

/** category -> band -> who bought it out of who was active */
export type BandReach = Record<string, Record<string, BandReachCell>>;

/** One person's, or the company's, band targets for one category: band -> share (0..1). null / absent = none. */
export type BandTargets = Record<string, number | null | undefined>;

/**
 * Ranks customers by net sales and cuts them into bands. Customers with net sales of zero or less are left out
 * (they were not buying). Ties go by account code so the result never depends on input order. Band i ends at
 * ceil(n x cumulative share), so with few customers the biggest still lands in the first band.
 */
export function customerBands(
    netSales: Record<string, number>,
    spec: BandSpec[] = DEFAULT_BANDS
): Record<string, string> {
    const ranked = Object.entries(netSales)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
    const n = ranked.length;
    const out: Record<string, string> = {};
    let cumulative = 0;
    let at = 0;
    spec.forEach((band, i) => {
        cumulative += band.share;
        const end = i === spec.length - 1 ? n : Math.min(n, Math.ceil(n * cumulative - 1e-9));
        for (; at < end; at++) out[ranked[at][0]] = band.key;
    });
    return out;
}

/** Each customer's net sales across every salesperson and every list (month) given. */
export function customerNetSales(months: SegLists[]): Record<string, number> {
    const out: Record<string, number> = {};
    for (const lists of months)
        for (const list of Object.values(lists))
            for (const [code, c] of Object.entries(list)) out[code] = (out[code] || 0) + (c.sales || 0);
    return out;
}

const bandKeysOf = (bands: Record<string, string>): string[] => [...new Set(Object.values(bands))];

/** For each salesperson, how many of their active customers (sales above zero that month) sit in each band. */
export function repMix(lists: SegLists, bands: Record<string, string>): Record<string, BandCounts> {
    const keys = bandKeysOf(bands);
    const out: Record<string, BandCounts> = {};
    for (const [rep, list] of Object.entries(lists)) {
        const mix: BandCounts = Object.fromEntries(keys.map((k) => [k, 0]));
        for (const [code, c] of Object.entries(list)) {
            const band = bands[code];
            if (band && c.sales > 0) mix[band]++;
        }
        out[rep] = mix;
    }
    return out;
}

/** The company's active customers per band (a customer under two salespeople counts once). */
export function companyMixOf(lists: SegLists, bands: Record<string, string>): BandCounts {
    const keys = bandKeysOf(bands);
    const mix: BandCounts = Object.fromEntries(keys.map((k) => [k, 0]));
    for (const [code, c] of Object.entries(merged(lists))) if (bands[code] && c.sales > 0) mix[bands[code]]++;
    return mix;
}

function merged(lists: SegLists): Record<string, { sales: number; cats: Record<string, number> }> {
    const out: Record<string, { sales: number; cats: Record<string, number> }> = {};
    for (const list of Object.values(lists))
        for (const [code, c] of Object.entries(list)) {
            const m = (out[code] = out[code] || { sales: 0, cats: {} });
            m.sales += c.sales || 0;
            for (const [cat, t] of Object.entries(c.cats || {})) m.cats[cat] = (m.cats[cat] || 0) + (t.sales || 0);
        }
    return out;
}

/** Per category and band: how many of the company's active customers in the band bought the category. */
export function bandReach(lists: SegLists, bands: Record<string, string>): BandReach {
    const keys = bandKeysOf(bands);
    const out: BandReach = {};
    const cells = (cat: string) =>
        (out[cat] = out[cat] || Object.fromEntries(keys.map((k) => [k, { buyers: 0, active: 0 }])));
    const customers = merged(lists);
    const active: BandCounts = Object.fromEntries(keys.map((k) => [k, 0]));
    for (const [code, c] of Object.entries(customers)) {
        const band = bands[code];
        if (!band || !(c.sales > 0)) continue;
        active[band]++;
        for (const [cat, sales] of Object.entries(c.cats)) if (sales > 0) cells(cat)[band].buyers++;
    }
    for (const cat of Object.keys(out)) for (const k of keys) out[cat][k].active = active[k];
    return out;
}

export interface ExpectedReach {
    /** the share the salesperson (or company) should reach; null when nothing sets one */
    target: number | null;
    /** true when band targets shaped it */
    weighted: boolean;
}

const has = (v: number | null | undefined): v is number => typeof v === 'number' && !Number.isNaN(v);

/**
 * Expected reach = sum(band target x active customers in the band) / active customers.
 *
 * A band without a target uses the category target. A band with neither is left out of the weighting, so the
 * answer is the average over the bands that do have a target. With no band target at all the answer is the
 * category target, untouched, and `weighted` is false: behaviour before band targets existed.
 */
export function expectedReach(
    bandTargets: BandTargets | undefined | null,
    categoryTarget: number | null | undefined,
    mix: BandCounts | undefined | null
): ExpectedReach {
    const flat: ExpectedReach = { target: has(categoryTarget) ? categoryTarget : null, weighted: false };
    if (!bandTargets || !mix || !Object.values(bandTargets).some(has)) return flat;
    let sum = 0;
    let customers = 0;
    for (const [band, n] of Object.entries(mix)) {
        if (!(n > 0)) continue;
        const t = has(bandTargets[band]) ? bandTargets[band] : has(categoryTarget) ? categoryTarget : null;
        if (t === null) continue;
        sum += t * n;
        customers += n;
    }
    if (!customers) return flat;
    return { target: sum / customers, weighted: true };
}

/** The keys of the months that are complete: a month ending after the last sales line loaded is in progress. */
export function fullMonths(months: { key: string; end: string }[], lastLine: string | undefined | null): string[] {
    return months
        .filter((m) => !lastLine || !m.end || m.end <= lastLine.slice(0, 10))
        .map((m) => m.key)
        .sort();
}

/**
 * Starting band targets: the company's reach in each band, averaged over the last three FULL months given
 * (the caller never passes a month in progress), in whole percent and at least 1%. Nothing for a band nobody
 * in buys. Bands that already have a target in `existing` are not suggested again. Never applied on its own.
 */
export function suggestBandTargets(
    reach: Record<string, BandReach>,
    complete: string[],
    existing: Record<string, BandTargets> = {}
): Record<string, Record<string, number>> {
    const window = [...complete].sort().slice(-3);
    const values: Record<string, Record<string, number[]>> = {};
    for (const month of window)
        for (const [cat, bands] of Object.entries(reach[month] || {}))
            for (const [band, c] of Object.entries(bands))
                if (c.active > 0)
                    ((values[cat] = values[cat] || {})[band] = values[cat][band] || []).push(c.buyers / c.active);
    const out: Record<string, Record<string, number>> = {};
    for (const [cat, bands] of Object.entries(values))
        for (const [band, xs] of Object.entries(bands)) {
            if (has(existing[cat]?.[band])) continue;
            const avg = xs.reduce((a, b) => a + b, 0) / xs.length;
            if (!(avg > 0)) continue;
            (out[cat] = out[cat] || {})[band] = Math.max(1, Math.round(avg * 100)) / 100;
        }
    return out;
}

/** The document written to cycles/{dataKey}/segments/summary. Counts only. */
export interface SegmentsSummary {
    /** band keys, biggest spenders first (a Firestore map has no order of its own) */
    order: string[];
    /** how many customers fell in each band */
    bands: BandCounts;
    /** month -> salesperson -> active customers per band */
    mix: Record<string, Record<string, BandCounts>>;
    /** month -> the company's active customers per band */
    companyMix: Record<string, BandCounts>;
    /** month -> category -> band -> buyers / active */
    reach: Record<string, BandReach>;
    /** how the bands were made, e.g. 'spend' until real customer types exist */
    source: string;
}

/**
 * Builds the summary for one calendar. `monthLists` is every loaded month's customer lists; `fullKeys` the
 * months complete enough to rank customers on (a month in progress never shapes the bands).
 */
export function buildSegments(
    monthLists: Record<string, SegLists>,
    fullKeys: string[],
    spec: BandSpec[] = DEFAULT_BANDS
): SegmentsSummary {
    const bands = customerBands(
        customerNetSales(fullKeys.filter((k) => monthLists[k]).map((k) => monthLists[k])),
        spec
    );
    const counts: BandCounts = Object.fromEntries(spec.map((b) => [b.key, 0]));
    for (const b of Object.values(bands)) counts[b]++;
    const mix: SegmentsSummary['mix'] = {};
    const companyMix: SegmentsSummary['companyMix'] = {};
    const reach: SegmentsSummary['reach'] = {};
    for (const [month, lists] of Object.entries(monthLists)) {
        mix[month] = repMix(lists, bands);
        companyMix[month] = companyMixOf(lists, bands);
        reach[month] = bandReach(lists, bands);
    }
    return { order: spec.map((b) => b.key), bands: counts, mix, companyMix, reach, source: 'spend' };
}
